/**
 * Table Header Component
 *
 * Renders the table header with all header groups.
 */
import { DraggableHeader } from "./DraggableHeader";
import type { AppHeader, AppHeaderGroup, AppTable } from "../features";
import type { RowData } from "@tanstack/react-table";

type TableHeaderProps<T extends RowData> = {
  table: AppTable<T>
  hasNoVisibleColumns: boolean
  enableSorting: boolean
  onChangeSort?: (field: string) => void
  enableColumnResizing: boolean
  enableColumnPinning: boolean
  enableColumnOrdering: boolean
  enableColumnFiltering: boolean
};

export const TableHeader = <T extends RowData>({
  table,
  hasNoVisibleColumns,
  enableSorting,
  onChangeSort,
  enableColumnResizing,
  enableColumnPinning,
  enableColumnOrdering,
  enableColumnFiltering,
}: TableHeaderProps<T>) => {
  if (hasNoVisibleColumns) return null;

  return (
    <thead className="bg-gray-50">
      {table.getHeaderGroups().map((headerGroup: AppHeaderGroup<T>) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header: AppHeader<T>, index: number) => (
            <DraggableHeader<T>
              key={header.id}
              header={header}
              enableSorting={enableSorting}
              onChangeSort={onChangeSort}
              enableColumnResizing={enableColumnResizing}
              enableColumnPinning={enableColumnPinning}
              enableColumnOrdering={enableColumnOrdering}
              enableColumnFiltering={enableColumnFiltering}
              isLastColumn={index === headerGroup.headers.length - 1}
            />
          ))}
        </tr>
      ))}
    </thead>
  );
};
